// pages/LandingPages/MainPage/sections/History.js

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

// Images
import founderImage from "assets/images/reyes-catolicos.jpg";

const timelineEvents = [
  {
    year: "1961",
    title: "Fundación",
    text: "José Moreno Martín abre la primera gestoría en la calle Reyes Católicos, Granada.",
  },
  {
    year: "1970",
    title: "Gestoría Alhambra",
    text: "Se inaugura una segunda oficina en la calle Ganivet, ampliando servicios.",
  },
  {
    year: "1997",
    title: "Unificación",
    text: "Ambas oficinas se unen como Gestorías Moreno Martín Alhambra S.L.P. en Av. Divina Pastora.",
  },
  {
    year: "Hoy",
    title: "3ª Generación",
    text: "Tres hijos del fundador dirigen la empresa manteniendo los valores originales.",
  },
];

const values = [
  {
    icon: "handshake",
    title: "Compromiso",
    text: "Nos involucramos en cada caso como si fuera propio",
  },
  {
    icon: "verified_user",
    title: "Confianza",
    text: "Más de 2.000 clientes avalan nuestra trayectoria",
  },
  {
    icon: "people",
    title: "Cercanía",
    text: "Trato directo y personalizado con cada cliente desde hace más de 60 años",
  },
];

function History() {
  return (
    <MKBox
      component="section"
      variant="gradient"
      bgColor="dark"
      position="relative"
      py={6}
      mx={-2}
      mb={6}
    >
      <Container>
        {/* Valores */}
        <MKBox mb={4}>
          <MKTypography variant="h2" color="white" textAlign="center" mb={3}>
            Nuestros Valores
          </MKTypography>
          <Grid container spacing={4} justifyContent="center">
            {values.map(({ icon, title, text }) => (
              <Grid item xs={12} sm={4} key={title}>
                <MKBox textAlign="center" px={2}>
                  <MKBox
                    display="inline-flex"
                    justifyContent="center"
                    alignItems="center"
                    width="70px"
                    height="70px"
                    borderRadius="50%"
                    bgColor="info"
                    color="white"
                    mb={2}
                    sx={{
                      background: ({ palette: { gradients } }) =>
                        gradients.info.main || gradients.info,
                    }}
                  >
                    <Icon sx={{ fontSize: "2rem !important", color: "white !important" }}>
                      {icon}
                    </Icon>
                  </MKBox>
                  <MKTypography variant="h5" component="h3" color="white" mb={1}>
                    {title}
                  </MKTypography>
                  <MKTypography variant="body2" color="white" opacity={0.9}>
                    {text}
                  </MKTypography>
                </MKBox>
              </Grid>
            ))}
          </Grid>
        </MKBox>
        {/* Encabezado */}
        <MKBox textAlign="center" mt={6} mb={6}>
          <MKTypography variant="h2" color="white" mb={1}>
            Nuestra Historia
          </MKTypography>
          <MKTypography
            variant="body1"
            color="white"
            opacity={0.9}
            sx={{ maxWidth: 800, mx: "auto" }}
          >
            Más de 60 años acompañando a particulares, autónomos y empresas en Granada
          </MKTypography>
        </MKBox>

        <Grid container spacing={4} alignItems="stretch">
          {/* Timeline */}
          <Grid item xs={12} md={8}>
            <MKBox
              position="relative"
              pl={{ xs: 4, md: 5 }}
              height="100%"
              display="flex"
              flexDirection="column"
              justifyContent="space-around"
            >
              {/* Línea vertical */}
              <MKBox
                sx={{
                  position: "absolute",
                  left: { xs: "9.5px", md: "11.5px" },
                  top: 0,
                  bottom: 0,
                  width: "3px",
                  backgroundColor: "info.main",
                  borderRadius: "2px",
                }}
              />
              {timelineEvents.map(({ year, title, text }) => (
                <MKBox key={year} position="relative">
                  {/* Punto en la línea */}
                  <MKBox
                    sx={{
                      position: "absolute",
                      left: { xs: "-28px", md: "-34px" },
                      top: "4px",
                      width: "14px",
                      height: "14px",
                      borderRadius: "50%",
                      backgroundColor: "info.main",
                      border: "3px solid white",
                      boxShadow: "0 0 0 3px rgba(243,146,0,0.3)",
                    }}
                  />
                  <MKTypography
                    variant="button"
                    color="info"
                    fontWeight="bold"
                    textTransform="uppercase"
                    display="block"
                    mb={0.5}
                  >
                    {year} — {title}
                  </MKTypography>
                  <MKTypography variant="body2" color="white" opacity={0.8}>
                    {text}
                  </MKTypography>
                </MKBox>
              ))}
            </MKBox>
          </Grid>

          {/* Imagen */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                borderRadius: "xl",
                overflow: "hidden",
                maxHeight: "400px",
              }}
            >
              <img
                src={founderImage}
                alt="Edificio la Paz, Granada — primera sede de Gestoría Moreno"
                width="400"
                height="280"
                loading="lazy"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "top 30%",
                }}
              />
            </Card>
            <MKTypography
              variant="caption"
              color="white"
              opacity={0.85}
              display="block"
              textAlign="center"
              mt={1}
            >
              Edificio la Paz, Granada — primera sede de Gestoría Moreno
            </MKTypography>
          </Grid>
        </Grid>

        {/* CTA */}
        <MKBox textAlign="center" mt={6}>
          <MKTypography variant="h5" color="white" mb={2}>
            ¿Necesitas un asesor de confianza?
          </MKTypography>
          <MKButton
            component="a"
            href="#contacto"
            variant="gradient"
            color="info"
            size="large"
            sx={{ textTransform: "none", borderRadius: "25px", px: 4 }}
          >
            Contacta con nosotros
          </MKButton>
        </MKBox>
      </Container>
    </MKBox>
  );
}

export default History;
